// ABOUTME: Assigns canonical Stores to WhatsApp pre-registration Regions.
// ABOUTME: Prevents one Store from being configured in more than one Region.
import React from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    TextField,
    Tooltip,
    Typography
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";

import { WhatsappPreRegistrationLabels } from "./whatsappPreRegistrationLabels";
import {
    isWhatsappPreRegistrationRegionIdentifier,
    removeWhatsappPreRegistrationRegion,
    updateWhatsappPreRegistrationRegion,
    WhatsappPreRegistrationConfig,
    WhatsappPreRegistrationOptions
} from "./whatsappPreRegistrationSettingsModel";

interface WhatsappPreRegistrationRegionStoreEditorProps {
    config: WhatsappPreRegistrationConfig;
    stores: WhatsappPreRegistrationOptions["stores"];
    labels: WhatsappPreRegistrationLabels;
    onChange: (config: WhatsappPreRegistrationConfig) => void;
}

const getSelectValues = (value: unknown) =>
    (Array.isArray(value) ? value : String(value || "").split(","))
        .map((item) => String(item || "").trim())
        .filter(Boolean);

const WhatsappPreRegistrationRegionStoreEditor = ({
    config,
    stores,
    labels,
    onChange
}: WhatsappPreRegistrationRegionStoreEditorProps) => {
    const addRegion = () => {
        let sequence = config.regions.length + 1;
        let id = `region-${sequence}`;
        while (config.regions.some((region) => region.id === id)) {
            sequence += 1;
            id = `region-${sequence}`;
        }
        onChange({
            ...config,
            regions: [
                ...config.regions,
                { id, name: "", active: true, storeIds: [] }
            ]
        });
    };

    return (
        <Paper elevation={0} style={{ padding: 24 }}>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
            >
                <Typography variant="h6">{labels.regions}</Typography>
                <Button
                    startIcon={<AddIcon />}
                    onClick={addRegion}
                    color="primary"
                >
                    {labels.addRegion}
                </Button>
            </Box>
            {config.regions.length === 0 && (
                <Alert severity="info">{labels.noRegions}</Alert>
            )}
            {config.regions.map((region, index) => {
                const invalidRegionIdentifier =
                    !!region.id &&
                    !isWhatsappPreRegistrationRegionIdentifier(region.id);
                const storesAssignedElsewhere = new Set(
                    config.regions
                        .filter((_, regionIndex) => regionIndex !== index)
                        .reduce<string[]>(
                            (storeIds, item) => [...storeIds, ...item.storeIds],
                            []
                        )
                );
                return (
                    <Box
                        key={index}
                        mb={2}
                        p={2}
                        border="1px solid #e0e0e0"
                        borderRadius={4}
                    >
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label={labels.regionId}
                                    value={region.id}
                                    error={invalidRegionIdentifier}
                                    helperText={
                                        invalidRegionIdentifier
                                            ? labels.regionIdentifierHelp
                                            : ""
                                    }
                                    inputProps={{ maxLength: 100 }}
                                    onChange={(event) =>
                                        onChange(
                                            updateWhatsappPreRegistrationRegion(
                                                config,
                                                index,
                                                { id: event.target.value }
                                            )
                                        )
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label={labels.regionName}
                                    value={region.name}
                                    onChange={(event) =>
                                        onChange(
                                            updateWhatsappPreRegistrationRegion(
                                                config,
                                                index,
                                                { name: event.target.value }
                                            )
                                        )
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel>{labels.stores}</InputLabel>
                                    <Select
                                        multiple
                                        value={region.storeIds}
                                        onChange={(event) =>
                                            onChange(
                                            updateWhatsappPreRegistrationRegion(
                                                    config,
                                                    index,
                                                    {
                                                        storeIds: getSelectValues(
                                                            event.target.value
                                                        )
                                                    }
                                                )
                                            )
                                        }
                                        label={labels.stores}
                                        renderValue={(selected) =>
                                            getSelectValues(selected)
                                                .map(
                                                    (storeId) =>
                                                        stores.find(
                                                            (store) =>
                                                                store.id ===
                                                                storeId
                                                        )?.name || storeId
                                                )
                                                .join(", ")
                                        }
                                    >
                                        {stores.map((store) => (
                                            <MenuItem
                                                key={store.id}
                                                value={store.id}
                                                disabled={storesAssignedElsewhere.has(
                                                    store.id
                                                )}
                                            >
                                                <Checkbox
                                                    checked={region.storeIds.includes(
                                                        store.id
                                                    )}
                                                />
                                                <ListItemText
                                                    primary={`${store.name}${
                                                        store.available ===
                                                        false
                                                            ? ` (${labels.unavailable})`
                                                            : ""
                                                    }`}
                                                />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={10} md={1}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={region.active}
                                            onChange={(event) =>
                                                onChange(
                                                    updateWhatsappPreRegistrationRegion(
                                                        config,
                                                        index,
                                                        {
                                                            active:
                                                                event.target
                                                                    .checked
                                                        }
                                                    )
                                                )
                                            }
                                            color="primary"
                                        />
                                    }
                                    label={labels.active}
                                />
                            </Grid>
                            <Grid item xs={2} md={1}>
                                <Tooltip title={labels.delete}>
                                    <IconButton
                                        onClick={() =>
                                            onChange(
                                                removeWhatsappPreRegistrationRegion(
                                                    config,
                                                    index
                                                )
                                            )
                                        }
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Box>
                );
            })}
        </Paper>
    );
};

export default WhatsappPreRegistrationRegionStoreEditor;
